import { memo, useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getBlockNameByType } from 'lib/blocks';
import { addInitialDataToBlockDefaultData } from 'lib/editor';
import { AIGenerateContentBlockIndicator } from 'components/aiTools';
import { Icon } from 'components/common/icons';
import { useEditorContext, useEditorDispatch } from 'components/editor';
import { editorSocket } from 'components/editor/socket';
import { blockIcons, defaultBlockData } from 'components/editor/blocks';
import { EditorBlockAdderAnimate } from '.';

import type { BlockType } from 'shared-types';
import type { getEditorBlockTypes } from 'lib/editor';

interface Props {
  active: boolean;
  blockTypes: ReturnType<typeof getEditorBlockTypes>;
  chapterId: number;
  courseId: number;
  handleMoveBlocks: (position: number) => void;
  isBlockMoveView: boolean;
  position: number;
}

const EditorBlockAdder = ({
  active,
  blockTypes,
  chapterId,
  courseId,
  isBlockMoveView,
  handleMoveBlocks,
  position
}: Props) => {
  const { editorNextBlockId } = useEditorContext();

  const editorDispatch = useEditorDispatch();

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const adderElement = useRef<HTMLDivElement>(undefined);
  const blockSubTypeBackClicked = useRef(false);

  const [blockSubTypes, setBlockSubTypes] = useState<BlockType[]>(null);

  useEffect(() => {
    if (active) {
      document.addEventListener('click', handleOutsideMouseClick);
    } else if (!active) {
      document.removeEventListener('click', handleOutsideMouseClick);
      setBlockSubTypes(null);
      blockSubTypeBackClicked.current = false;
    }

    return () => document.removeEventListener('click', handleOutsideMouseClick);
  }, [active]);

  const handleAdderClick = (ev) => {
    ev.stopPropagation();

    const position = parseInt(
      ev.currentTarget.getAttribute('data-position'),
      10
    );
    if (isBlockMoveView) handleMoveBlocks(position);
    else editorSocket.activateAdder({ position });
  };

  const handleSubAdderClick = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();

    const blockSubTypes = ev.currentTarget
      .getAttribute('data-sub-types')
      .split(',');

    setBlockSubTypes(blockSubTypes);
  };

  const handleBlockClick = (ev) => {
    const blockType = ev.currentTarget.getAttribute('data-type');

    const position = parseInt(
      ev.currentTarget.getAttribute('data-position'),
      10
    );

    // get all language version keys from the course
    const languageKeys = [course.language];
    if (course.languageVersions) {
      Object.keys(course.languageVersions).forEach((key) => {
        if (!languageKeys.includes(key)) languageKeys.push(key);
      });
    }

    // build locales for all languages
    const defaultLocales = {};
    languageKeys.forEach((language) => {
      defaultLocales[language] = defaultBlockData[blockType].locales[language];
    });

    let defaultData = {
      ...defaultBlockData[blockType],
      locales: defaultLocales
    };

    // optionally populate fields with initial data
    defaultData = addInitialDataToBlockDefaultData({
      blockType,
      courseLanguage: course.language,
      defaultData
    });

    setBlockSubTypes(null);
    blockSubTypeBackClicked.current = false;

    editorSocket.addBlock({
      // treat singlechoice block as multiplechoice block
      blockType: blockType === 'singlechoice' ? 'multiplechoice' : blockType,
      chapterId,
      courseId,
      defaultData,
      localBlockId: editorNextBlockId,
      missingTranslations: Object.keys(course.languageVersions),
      position
    });

    editorDispatch({
      type: 'updateContext',
      update: { editorNextBlockId: editorNextBlockId - 1 }
    });
  };

  const handleOutsideMouseClick = (ev) => {
    if (blockSubTypeBackClicked.current) {
      blockSubTypeBackClicked.current = false;
      return;
    }

    if (adderElement.current && adderElement.current.contains(ev.target)) {
      return;
    }

    setBlockSubTypes(null);
    blockSubTypeBackClicked.current = false;
    editorSocket.deactivateAdders();
  };

  const handleSubtypesBack = () => {
    setBlockSubTypes(null);
    blockSubTypeBackClicked.current = true;
  };

  return (
    <div
      className="editor-block-adder"
      data-position={position}
      ref={adderElement}
    >
      {active ? (
        <EditorBlockAdderAnimate>
          <div className="block-buttons">
            <div
              className={`block-buttons-inner ${
                blockSubTypes ? 'block-buttons-inner-subtypes' : ''
              }`}
            >
              {blockSubTypes ? (
                <EditorBlockAdderAnimate>
                  <button
                    aria-label={messages.commonGoBack}
                    className="block-subtypes-back"
                    onClick={handleSubtypesBack}
                  >
                    <Icon icon="chevron-left" />
                  </button>
                </EditorBlockAdderAnimate>
              ) : null}

              {blockSubTypes
                ? blockSubTypes.map((type, i) => (
                    <EditorBlockAdderAnimate key={i}>
                      <div className="block-button-wrapper">
                        <button
                          className="block-button"
                          data-type={type}
                          data-position={position}
                          onClick={handleBlockClick}
                        >
                          <div
                            className="block-button-icon"
                            style={
                              type === 'embedlink' ||
                              type === 'embedcode' ||
                              type === 'h5p' ||
                              type === 'scorm'
                                ? {
                                    fontSize: '2.2rem',
                                    marginBottom: 2
                                  }
                                : {}
                            }
                          >
                            <AIGenerateContentBlockIndicator type={type} />

                            <Icon icon={blockIcons[type]} type="block" />
                          </div>

                          {getBlockNameByType({ messages, type })}
                        </button>
                      </div>
                    </EditorBlockAdderAnimate>
                  ))
                : blockTypes.map((type, i) => {
                    const isString = typeof type === 'string';

                    const typeString = isString ? type : type.category;

                    const onClick = isString
                      ? handleBlockClick
                      : handleSubAdderClick;

                    return (
                      <EditorBlockAdderAnimate key={i}>
                        <div className="block-button-wrapper">
                          <button
                            className="block-button"
                            data-type={typeString}
                            data-position={position}
                            onClick={onClick}
                            {...(typeof type !== 'string'
                              ? { 'data-sub-types': type.subTypes }
                              : {})}
                          >
                            {isString ? (
                              <AIGenerateContentBlockIndicator type={type} />
                            ) : type.category === 'task' ? (
                              <AIGenerateContentBlockIndicator />
                            ) : null}

                            <div className="block-button-icon">
                              <Icon
                                icon={blockIcons[typeString]}
                                type="block"
                              />
                            </div>

                            {getBlockNameByType({
                              messages,
                              type: typeString as BlockType
                            })}

                            {typeof type !== 'string' ? (
                              <div className="subtypes-icon">
                                <Icon icon="chevron-right" />
                              </div>
                            ) : null}
                          </button>
                        </div>
                      </EditorBlockAdderAnimate>
                    );
                  })}
            </div>
          </div>
        </EditorBlockAdderAnimate>
      ) : (
        <div
          className={`add-button-wrapper ${
            isBlockMoveView ? 'add-button-wrapper-move' : ''
          }`}
        >
          <button
            aria-label={messages.editorBlockAdderAddBlock}
            className="add-button"
            data-position={position}
            onClick={handleAdderClick}
          >
            {isBlockMoveView ? (
              <span className="dragged-label">
                {messages.editorBlockAdderDropHere}
              </span>
            ) : (
              <Icon icon="plus" />
            )}
          </button>
        </div>
      )}

      <style jsx>{`
        .editor-block-adder {
          border-radius: ${theme.cardBorderRadius};
          margin: 5px 40px;
          text-align: center;
        }
        .add-button-wrapper {
          border: 2px dashed transparent;
          border-radius: ${theme.buttonBorderRadius};
          padding: 0 10px;
        }
        .add-button-wrapper-move {
          border-color: ${theme.lightGreyDarkActive};
          padding: 0;
        }
        .add-button {
          background-color: white;
          border: none;
          border-radius: ${theme.buttonBorderRadius};
          color: #aaa;
          cursor: pointer;
          display: block;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 400;
          padding: 5px 10px;
          width: 100%;
          height: 100%;
          transition: 0.2s background-color, 0.2s color;
        }
        .add-button:hover {
          background-color: ${theme.lightGrey};
          color: ${theme.textColorLighter};
        }
        .block-buttons {
          border: 2px dashed ${theme.lightGreyDarkActive};
          border-radius: ${theme.cardBorderRadius};
          padding: 10px 5px;
        }
        .block-buttons-inner {
          //background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          height: 100%;
          justify-content: center;
          position: relative;
        }
        .block-buttons-inner-subtypes {
          padding: 0 20px;
        }
        .block-subtypes-back {
          background-color: ${theme.lightGrey};
          border: 0;
          border-radius: ${theme.cardBorderRadius};
          color: ${theme.textColorLighter};
          cursor: pointer;
          font-size: 1.8rem;
          height: 100%;
          min-height: 70px;
          padding: 0 15px;
          transition: background-color 0.2s, color 0.2s;
          width: 100px;
        }
        .block-subtypes-back:focus,
        .block-subtypes-back:hover {
          background-color: ${theme.lightGreyActive};
          color: ${theme.textColor};
        }
        .block-button-wrapper {
          align-items: center;
          display: flex;
          height: 100%;
          justify-content: center;
          position: relative;
        }
        .block-button {
          background-color: ${theme.lightGrey};
          border: none;
          border-radius: ${theme.cardBorderRadius};
          color: ${theme.textColorLighter};
          cursor: pointer;
          display: inline-block;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 600;
          height: 100%;
          //margin: 5px 3px;
          min-height: 70px;
          padding: 7px;
          position: relative;
          transition: background-color 0.2s, color 0.2s;
          width: 100px;
          word-break: break-word;
        }
        .block-button:active,
        .block-button:hover {
          background-color: ${theme.lightGreyActive};
          color: ${theme.textColor};
        }
        .block-button-icon {
          font-size: 2.8rem;
          line-height: 1;
        }
        .subtypes-icon {
          color: ${theme.textColorLighter};
          display: inline-block;
          font-size: 1.1rem;
          margin-left: 5px;
          position: relative;
          top: -1px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .block-buttons-inner-subtypes {
            padding: 0 80px;
          }
          .block-subtypes-back {
            position: absolute;
            left: 5px;
            top: 0;
            bottom: 0;
            width: 50px;
          }
        }
      `}</style>
    </div>
  );
};

export default memo(EditorBlockAdder);
